(function () {
  'use strict';

  const operationVersions = new Map();

  class CartController {
    static createDebouncedTask(callback, delay) {
      let timer = null;

      return {
        schedule(...args) {
          window.clearTimeout(timer);
          timer = window.setTimeout(function () {
            timer = null;
            callback(...args);
          }, delay);
        },

        cancel() {
          window.clearTimeout(timer);
          timer = null;
        },

        async flush(...args) {
          this.cancel();
          return callback(...args);
        }
      };
    }

    static createAutosaveField(options) {
      const settings = options || {};
      const task = this.createDebouncedTask(
        function (field) {
          return settings.onSave?.(field);
        },
        settings.delay ?? 500
      );

      function update(field) {
        if (!field) return;

        const count = settings.getCount?.(field) || null;
        CartController.updateCharacterCount(field, count, {
          maximum: settings.maximum
        });

        settings.onInput?.(field);
      }

      return {
        handleInput(field) {
          if (!field) return;
          update(field);
          task.schedule(field);
        },

        initialise(field) {
          update(field);
        },

        cancel() {
          task.cancel();
        },

        flush(field) {
          return task.flush(field);
        }
      };
    }



    static createLineControls(options) {
      const settings = options || {};
      const root = settings.root;
      const timers = new Map();

      if (!root || typeof settings.onChange !== 'function') {
        return { destroy() {} };
      }

      const selectors = {
        minus: settings.minusSelector,
        plus: settings.plusSelector,
        remove: settings.removeSelector,
        quantity: settings.quantitySelector
      };

      function getInput(key, trigger) {
        if (typeof settings.getInput === 'function') {
          return settings.getInput(key, trigger);
        }

        return null;
      }

      function normaliseQuantity(value) {
        return Math.max(0, Number(value || 0));
      }

      function handleClick(event) {
        const minusButton = selectors.minus
          ? event.target.closest(selectors.minus)
          : null;
        const plusButton = selectors.plus
          ? event.target.closest(selectors.plus)
          : null;
        const removeButton = selectors.remove
          ? event.target.closest(selectors.remove)
          : null;

        if (removeButton) {
          settings.onChange(removeButton.dataset.key, 0, {
            source: 'remove',
            trigger: removeButton,
            line: Number(removeButton.dataset.line || 0)
          });
          return;
        }

        if (!minusButton && !plusButton) return;

        const button = minusButton || plusButton;
        const key = button.dataset.key;
        const input = getInput(key, button);

        if (!input) return;

        const currentQuantity = normaliseQuantity(input.value);
        const nextQuantity = minusButton
          ? Math.max(0, currentQuantity - 1)
          : currentQuantity + 1;

        input.value = String(nextQuantity);

        settings.onChange(key, nextQuantity, {
          source: minusButton ? 'minus' : 'plus',
          trigger: button,
          input,
          line: Number(button.dataset.line || input.dataset.line || 0)
        });
      }

      function handleChange(event) {
        const input = selectors.quantity
          ? event.target.closest(selectors.quantity)
          : null;

        if (!input) return;

        const key = input.dataset.key;
        window.clearTimeout(timers.get(key));

        const timer = window.setTimeout(function () {
          timers.delete(key);
          settings.onChange(key, normaliseQuantity(input.value), {
            source: 'input',
            trigger: input,
            input,
            line: Number(input.dataset.line || 0)
          });
        }, settings.delay ?? 250);

        timers.set(key, timer);
      }

      root.addEventListener('click', handleClick);
      root.addEventListener('change', handleChange);

      return {
        destroy() {
          root.removeEventListener('click', handleClick);
          root.removeEventListener('change', handleChange);
          timers.forEach(function (timer) {
            window.clearTimeout(timer);
          });
          timers.clear();
        }
      };
    }

    static createStatusRenderer(options) {
      const settings = options || {};
      let element = settings.element || null;

      function ensureElement() {
        if (element?.isConnected) return element;

        element = settings.createElement?.() || document.createElement('p');

        if (settings.className) element.className = settings.className;
        if (settings.dataset) {
          Object.entries(settings.dataset).forEach(function ([key, value]) {
            element.dataset[key] = value;
          });
        }

        element.setAttribute('role', settings.role || 'status');
        element.setAttribute('aria-live', settings.live || 'polite');
        element.hidden = true;

        if (settings.before) {
          settings.before.before(element);
        } else if (settings.parent) {
          settings.parent.prepend(element);
        }

        return element;
      }

      return {
        get element() {
          return ensureElement();
        },

        show(message, options) {
          const status = ensureElement();
          const display = options || {};

          status.setAttribute('role', display.role || settings.role || 'status');
          status.setAttribute('aria-live', display.live || settings.live || 'polite');
          status.hidden = false;
          window.CartUI.setStatus(status, message || '', display.clearAfter || 0);
          return status;
        },

        saving(message) {
          return this.show(message || 'Saving…');
        },

        success(message, clearAfter) {
          return this.show(message, {
            clearAfter: clearAfter ?? 1600,
            role: 'status',
            live: 'polite'
          });
        },

        error(error, fallback) {
          return this.show(CartController.errorMessage(error, fallback), {
            role: 'alert',
            live: 'assertive'
          });
        },

        clear() {
          if (!element) return;
          window.CartUI.setStatus(element, '');
          element.hidden = true;
        },

        remove() {
          element?.remove();
          element = null;
        }
      };
    }

    static beginOperation(scope) {
      if (!scope) return 0;

      const version = (operationVersions.get(scope) || 0) + 1;
      operationVersions.set(scope, version);
      return version;
    }

    static isLatestOperation(scope, version) {
      return !scope || operationVersions.get(scope) === version;
    }

    static createLineState(options) {
      const settings = options || {};
      const scope = `${settings.scopePrefix || 'cart-line'}:${settings.key || ''}`;
      const version = this.beginOperation(scope);
      const input = settings.quantityInput || null;
      const confirmedQuantity = Number(
        input?.dataset.confirmedQuantity ||
          input?.defaultValue ||
          input?.value ||
          0
      );

      return {
        scope,
        version,
        confirmedQuantity,

        isLatest() {
          return CartController.isLatestOperation(scope, version);
        },

        setLoading(loading) {
          if (!settings.busyTarget) return;

          window.CartUI.setBusy(
            settings.busyTarget,
            loading,
            settings.busyOptions || {}
          );
        },

        confirm(quantity) {
          if (!input) return;

          const value = String(Number(quantity || 0));
          input.value = value;
          input.defaultValue = value;
          input.dataset.confirmedQuantity = value;
        },

        rollback() {
          if (!input) return;

          const value = String(confirmedQuantity);
          input.value = value;
          input.defaultValue = value;
          input.dataset.confirmedQuantity = value;
        }
      };
    }

    static errorMessage(error, fallback) {
      return error?.message || fallback || 'The basket could not be updated.';
    }

    static updateCharacterCount(input, count, options) {
      if (!input || !count) return;

      const settings = options || {};
      const length = input.value.length;

      count.textContent = settings.maximum
        ? `${length}/${settings.maximum}`
        : String(length);
    }

    static async saveOrderNote(options) {
      const settings = options || {};
      const textarea = settings.textarea;

      if (!textarea) return null;

      const version = this.beginOperation(textarea);
      const status = settings.statusRenderer || this.createStatusRenderer({ element: settings.status });
      status.saving();

      try {
        const cart = await window.CartServices.saveOrderNote(
          textarea.value
        );

        if (!this.isLatestOperation(textarea, version)) return cart;

        window.CartUI.dispatchUpdated(
          cart,
          settings.source || 'cart:note'
        );
        status.success(
          settings.successMessage || 'Note saved',
          settings.clearAfter ?? 1600
        );

        return cart;
      } catch (error) {
        if (this.isLatestOperation(textarea, version)) {
          console.error(error);
          status.error(
            error,
            settings.errorMessage || 'The note could not be saved.'
          );
        }
        throw error;
      }
    }

    static async saveGiftMessage(options) {
      const settings = options || {};
      const textarea = settings.textarea;

      if (!textarea || settings.enabled === false) return null;

      const version = this.beginOperation(textarea);
      const status = settings.statusRenderer || this.createStatusRenderer({ element: settings.status });
      status.saving();

      try {
        const cart = await window.CartServices.saveGiftAttributes(
          true,
          textarea.value
        );

        if (!this.isLatestOperation(textarea, version)) return cart;

        window.CartUI.dispatchUpdated(
          cart,
          settings.source || 'cart:gift-message'
        );
        status.success(
          settings.successMessage || 'Message saved',
          settings.clearAfter ?? 1600
        );

        return cart;
      } catch (error) {
        if (this.isLatestOperation(textarea, version)) {
          console.error(error);
          status.error(
            error,
            settings.errorMessage || 'The message could not be saved.'
          );
        }
        throw error;
      }
    }

    static async changeLine(options) {
      const settings = options || {};

      if (!settings.key) return null;

      try {
        const cart = await window.CartApi.change(
          settings.key,
          settings.quantity
        );

        window.CartUI.dispatchUpdated(
          cart,
          settings.source || 'cart:line-change'
        );

        return cart;
      } catch (error) {
        console.error(error);
        throw error;
      }
    }
  }

  window.CartController = CartController;
})();
